import React, { useEffect, useState } from 'react';
import { Loan, LoanNote, RetentionOutcome } from '../types';
import { Button } from './ui/Button';
import { NoteTrail } from './NoteTrail';
import { ReleaseLoanModal } from './ReleaseLoanModal';
import { AlertTriangleIcon, ArrowLeftIcon, CalendarClockIcon, PhoneCallIcon, PhoneIcon, PhoneMissedIcon, PhoneOffIcon, RefreshCwIcon, TimerIcon, VoicemailIcon, XIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatRemainingTime, getClaimRemainingMs, getDaysInRetention, getInitials } from '../utils/retention';
interface ClaimedLoanViewProps {
  loan: Loan;
  hasNextLoan: boolean;
  onRelease: (loanId: string) => void;
  onReleaseAndClaimNext: (loanId: string) => void;
  onOpenLoan: (loan: Loan) => void;
  onAction: (loanId: string, outcome: RetentionOutcome, notes: string) => void;
}
const EASE = [0.23, 1, 0.32, 1] as const;
const CALL_DISPOSITIONS = [{
  label: 'Spoke with borrower',
  icon: PhoneCallIcon,
  note: 'Called borrower — spoke with them directly.'
}, {
  label: 'Left voicemail',
  icon: VoicemailIcon,
  note: 'Called borrower — left a voicemail.'
}, {
  label: 'No answer',
  icon: PhoneOffIcon,
  note: 'Called borrower — no answer.'
}, {
  label: 'Requested callback',
  icon: CalendarClockIcon,
  note: 'Called borrower — they requested a callback.'
}, {
  label: 'Wrong / bad number',
  icon: PhoneMissedIcon,
  note: 'Called borrower — wrong or disconnected number.'
}];
export function ClaimedLoanView({
  loan,
  hasNextLoan,
  onRelease,
  onReleaseAndClaimNext,
  onOpenLoan,
  onAction
}: ClaimedLoanViewProps) {
  const [, setTick] = useState(0);
  const [selectedOutcome, setSelectedOutcome] = useState<RetentionOutcome>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [isReleaseOpen, setIsReleaseOpen] = useState(false);
  const [isDispositioningCall, setIsDispositioningCall] = useState(false);
  const [notes, setNotes] = useState<LoanNote[]>(() => [{
    id: `${loan.id}-pushed`,
    author: loan.pushedBy,
    createdAt: loan.dateEntered,
    body: loan.retentionComment || 'Pushed to retention.'
  }]);
  const handleAddNote = (body: string) => {
    setNotes(current => [...current, {
      id: `${loan.id}-${current.length}`,
      author: loan.claimedBy ?? 'You',
      createdAt: new Date().toISOString(),
      body
    }]);
  };
  const [isSubmitting, setIsSubmitting] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  const handleConfirm = async () => {
    setIsSubmitting(true);
    await new Promise(resolve => setTimeout(resolve, 800));
    onAction(loan.id, selectedOutcome, resolutionNotes);
    setIsSubmitting(false);
    setSelectedOutcome(null);
    setResolutionNotes('');
  };
  const handleCancel = () => {
    setSelectedOutcome(null);
    setResolutionNotes('');
  };
  const days = getDaysInRetention(loan.dateEntered);
  const remainingMs = loan.claimedAt ? getClaimRemainingMs(loan.claimedAt) : 0;
  const isExpiringSoon = remainingMs < 5 * 60 * 1000;
  const isRunningLow = remainingMs < 15 * 60 * 1000;
  const remainingShare = Math.min(1, remainingMs / (60 * 60 * 1000));
  const timerChipTone = isExpiringSoon ? 'bg-rose-600 text-white' : isRunningLow ? 'bg-amber-500 text-brand-500' : 'bg-brand-500 text-white';
  const timerBarTone = isExpiringSoon ? 'bg-rose-600' : isRunningLow ? 'bg-amber-500' : 'bg-brand-500';
  const detailFields = [{
    label: 'Loan Amount',
    value: new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(loan.amount)
  }, {
    label: 'Date Entered',
    value: new Date(loan.dateEntered).toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    })
  }, {
    label: 'Current Owner',
    value: loan.loanOfficer
  }, {
    label: 'Contact',
    value: loan.borrowerPhone
  }];
  return <motion.article initial={{
    opacity: 0,
    y: 12,
    scale: 0.985
  }} animate={{
    opacity: 1,
    y: 0,
    scale: 1
  }} exit={{
    opacity: 0,
    y: -10
  }} transition={{
    duration: 0.28,
    ease: EASE
  }} aria-labelledby="claimed-borrower" className="flex flex-1 flex-col overflow-hidden bg-white" data-id="element-16" data-mp="15rgwxbxmbb0u">
      {/* Claim status + expiry */}
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-brand-200 bg-brand-50 px-6 py-3 sm:px-9" data-id="element-17" data-mp="i2xq70u7v6av">
        <div className="flex items-center gap-2.5" data-id="element-18" data-mp="d42wh3upnxbz">
          <span aria-hidden="true" className="flex h-7 w-7 items-center justify-center rounded-full bg-accent-500 text-[10px] font-semibold text-white" data-id="element-19" data-mp="d5nqmsd45e9b">
            {getInitials(loan.claimedBy ?? '')}
          </span>
          <span className="text-[13px] text-brand-400" data-id="element-20" data-mp="2oq53a8yc21l0">
            Claimed by{' '}
            <span className="font-semibold text-brand-500" data-id="element-21" data-mp="2tpzsm82gmj3y">
              {loan.claimedBy}
            </span>
          </span>
        </div>
        {loan.claimedAt && <p className={`flex items-center gap-3 rounded-full py-2 pl-4 pr-5 ${timerChipTone} ${isExpiringSoon ? 'animate-pulse' : ''}`} data-id="element-22" data-mp="211lulsbdfmse">
            <TimerIcon className="h-4 w-4 opacity-70" aria-hidden="true" data-id="element-23" data-mp="261853tzteoxi" />
            <span className="text-[10px] font-semibold uppercase tracking-[0.12em] opacity-70" data-id="element-24" data-mp="2ayqj3ysovucd">
              Claim expires in
            </span>
            <span className="font-mono text-[22px] font-bold leading-none tabular-nums" data-id="element-25" data-mp="2fskw6jdshb2h">
              {formatRemainingTime(loan.claimedAt)}
            </span>
          </p>}
      </div>

      {loan.claimedAt && <div className="h-1 w-full bg-brand-100" role="progressbar" aria-label="Time remaining on this claim" aria-valuemin={0} aria-valuemax={60} aria-valuenow={Math.ceil(remainingMs / 60000)} data-id="element-26" data-mp="1f99ddgpadico">
          <div className={`h-full ${timerBarTone} transition-[width] duration-1000 ease-linear`} style={{
        width: `${remainingShare * 100}%`
      }} data-id="element-27" data-mp="1oyj9d7n93l62" />
        </div>}

      <div className="grid flex-1 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)_380px]" data-id="element-28" data-mp="s91ty0ygukr6">
        <div className="flex min-w-0 flex-col items-start gap-6 px-6 py-7 sm:px-9 sm:py-8" data-id="element-29" data-mp="sbek31edo1fb">
          {/* Borrower headline */}
          <div className="min-w-0" data-id="element-30" data-mp="1196z04g6t726">
            <h2 id="claimed-borrower" data-id="element-31" data-mp="1185d14xmv05o">
              <button onClick={() => onOpenLoan(loan)} className="rounded text-left text-[34px] font-semibold leading-none tracking-[-0.03em] text-brand-500 transition-colors hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2 sm:text-[42px]" data-id="element-32" data-mp="2hnzt7gwt1tqo">
                {loan.borrowerName}
              </button>
            </h2>
            <div className="mt-2.5 flex flex-wrap items-center gap-3" data-id="element-33" data-mp="3lmvgcmime2ch">
              <span className="font-mono text-[13px] text-brand-300" data-id="element-34" data-mp="3qkettk7hub3d">
                {loan.loanNumber}
              </span>
              <span aria-hidden="true" className="h-3 w-px bg-brand-200" data-id="element-35" data-mp="i668mx7awml8" />
              <span className="flex items-center gap-2 text-[13px] text-brand-400" data-id="element-36" data-mp="m66uh2wqc02b">
                <span aria-hidden="true" className={`h-2 w-2 rounded-full ${days > 5 ? 'bg-rose-500' : days > 2 ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-37" data-mp="8eio7g95w5t9" />
                <span className="font-semibold text-brand-500" data-id="element-38" data-mp="112egm8rp7bk8">{days}</span>
                {days === 1 ? 'day' : 'days'} in retention
              </span>
            </div>
          </div>

          {/* Key details */}
          <dl className="grid w-full flex-1 grid-cols-1 gap-3 sm:grid-cols-2 sm:grid-rows-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,0.9fr)]" data-id="element-39" data-mp="31z5davin6tg1">
            {detailFields.map(field => <div key={field.label} className="flex flex-col justify-center rounded-2xl border border-brand-200 bg-brand-50 px-5 py-5" data-id="element-40" data-mp="2s8kcr2jereuh">
                <dt className="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-brand-300" data-id="element-41" data-mp="3kvdijzbnn4bc">
                  {field.label}
                </dt>
                <dd className="text-[22px] font-semibold leading-none tracking-[-0.02em] text-brand-500" data-id="element-42" data-mp="3ayinxrii5o2c">
                  {field.value}
                </dd>
              </div>)}
            <div className="flex flex-col rounded-2xl border border-brand-200 bg-brand-50 px-5 py-5 sm:col-span-2" data-id="element-43" data-mp="1zjdg693ikzbv">
              <dt className="mb-2 text-[10px] font-medium uppercase tracking-[0.12em] text-brand-300" data-id="element-44" data-mp="1vh8pnj8d4itg">
                Retention Reason
              </dt>
              <dd className="text-[15px] leading-relaxed text-brand-500" data-id="element-45" data-mp="b1kd9o1xp48x">
                {loan.retentionComment || 'No retention reason provided.'}
              </dd>
            </div>
          </dl>
        </div>

        {/* Note trail */}
        <div className="flex min-h-0 min-w-0 flex-col border-t border-brand-200 bg-brand-50/60 px-6 py-7 sm:px-8 lg:border-l lg:border-t-0" data-id="element-46" data-mp="oqum4v7cna79">
          <NoteTrail notes={notes} onAddNote={handleAddNote} data-id="element-47" data-mp="tppcci7nc11e" />
        </div>

        {/* Action rail */}
        <aside className="flex flex-col gap-6 border-t border-brand-200 bg-white px-6 py-7 sm:px-7 lg:border-l lg:border-t-0" data-id="element-48" data-mp="ynqvf352o0cv">
        <div className="flex flex-col gap-2.5" data-id="element-49" data-mp="12p2o5hj8t1pa">
          <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-brand-300" data-id="element-50" data-mp="17oxdhgnddj88">
            Reach the borrower
          </span>
          <a href={`tel:${loan.borrowerPhone.replace(/\D/g, '')}`} onClick={() => setIsDispositioningCall(true)} aria-expanded={isDispositioningCall} className="flex items-center justify-between gap-3 rounded-2xl bg-accent-500 px-5 py-4 text-white transition-colors hover:bg-accent-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2" data-id="element-51" data-mp="34kd404xmwuu5">
            <span data-id="element-52" data-mp="sy96yfkpvc33">
              <span className="block text-[10px] font-medium uppercase tracking-[0.12em] text-white/60" data-id="element-53" data-mp="sysr82xuacka">
                Call now
              </span>
              <span className="mt-0.5 block font-mono text-[19px] font-semibold tracking-tight" data-id="element-54" data-mp="11xml3ljscou5">
                {loan.borrowerPhone}
              </span>
            </span>
            <PhoneIcon className="h-5 w-5 shrink-0" aria-hidden="true" data-id="element-55" data-mp="16qpcpo505o7p" />
          </a>

          <AnimatePresence initial={false} data-id="element-56" data-mp="1bmxn6o898t8j">
            {isDispositioningCall && <motion.div initial={{
              opacity: 0,
              y: -6
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -6
            }} transition={{
              duration: 0.18,
              ease: EASE
            }} className="rounded-2xl border border-brand-200 bg-brand-50 p-3" data-id="element-57" data-mp="1godcishgtlwq">
                <div className="mb-2.5 flex items-center justify-between gap-2 px-1" data-id="element-58" data-mp="3oufhcfae51cd">
                  <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-brand-400" data-id="element-59" data-mp="3owi851jdayi5">
                    How did the call go?
                  </span>
                  <button onClick={() => setIsDispositioningCall(false)} aria-label="Dismiss call disposition" className="rounded p-0.5 text-brand-300 transition-colors hover:text-brand-500 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500" data-id="element-60" data-mp="3f3azn7tbz2du">
                    <XIcon className="h-3.5 w-3.5" data-id="element-61" data-mp="20wmus7oc3csd" />
                  </button>
                </div>
                <div className="grid grid-cols-1 gap-1.5" data-id="element-62" data-mp="24xzyab3yf3w4">
                  {CALL_DISPOSITIONS.map(option => <button key={option.label} onClick={() => {
                  handleAddNote(option.note);
                  setIsDispositioningCall(false);
                }} className="flex items-center gap-2.5 rounded-xl border border-brand-200 bg-white px-3.5 py-2.5 text-left text-[13px] font-medium text-brand-500 transition-colors hover:border-brand-300 hover:bg-brand-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500" data-id="element-63" data-mp="1r7cw28yzivva">
                      <option.icon className="h-3.5 w-3.5 shrink-0 text-brand-300" aria-hidden="true" data-id="element-64" data-mp="1d8wsgqwf5lhd" />
                      {option.label}
                    </button>)}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        <div className="flex flex-1 flex-col border-t border-brand-200 pt-6" data-id="element-65" data-mp="3cr87c1p0yws3">
        <AnimatePresence mode="wait" initial={false} data-id="element-66" data-mp="3qhlfo55twdhy">
          {!selectedOutcome ? <motion.div key="actions" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.18,
              ease: EASE
            }} className="flex flex-1 flex-col gap-3" data-id="element-67" data-mp="3lq3ebfzw8awg">
              <span className="text-[11px] font-medium uppercase tracking-[0.1em] text-brand-300" data-id="element-68" data-mp="9ajc3buw79hq">
                Resolve with outcome
              </span>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1" data-id="element-69" data-mp="2b0utgwynr2f6">
                <button onClick={() => setSelectedOutcome('Revived - New File')} className="group flex items-center gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-left text-emerald-900 transition-colors hover:border-emerald-300 hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2" data-id="element-70" data-mp="2b2xsg6hsg5w1">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 group-hover:bg-white/70" data-id="element-71" data-mp="1trsgoq05xuwy">
                    <RefreshCwIcon className="h-[18px] w-[18px]" data-id="element-72" data-mp="1xqri40mor9jn" />
                  </span>
                  <span data-id="element-73" data-mp="22pufcywfcbh2">
                    <span className="block text-[15px] font-semibold tracking-[-0.01em]" data-id="element-74" data-mp="27inkhnvt1lto">
                      Revive – New File
                    </span>
                    <span className="mt-0.5 block text-xs text-emerald-800/70" data-id="element-75" data-mp="1a2qgzcru6ve9">
                      Restart the process from App Started.
                    </span>
                  </span>
                </button>
                <button onClick={() => setSelectedOutcome('Revived - Previous Stage')} className="group flex items-center gap-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-5 py-4 text-left text-emerald-900 transition-colors hover:border-emerald-300 hover:bg-emerald-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-500 focus-visible:ring-offset-2" data-id="element-76" data-mp="17gr5zoop6ip6">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 group-hover:bg-white/70" data-id="element-77" data-mp="jrogjuntnlk4">
                    <ArrowLeftIcon className="h-[18px] w-[18px]" data-id="element-78" data-mp="9vlcukdbnev2" />
                  </span>
                  <span data-id="element-79" data-mp="5t88o0e83p0e">
                    <span className="block text-[15px] font-semibold tracking-[-0.01em]" data-id="element-80" data-mp="5sgbil4z9j80">
                      Revive – Previous Stage
                    </span>
                    <span className="mt-0.5 block text-xs text-emerald-800/70" data-id="element-81" data-mp="3nc6t64cqezin">
                      Return the file to its previous stage.
                    </span>
                  </span>
                </button>
              </div>
              <div className="lg:mt-auto lg:pt-5" data-id="element-82" data-mp="3df12uybxzcjh">
                <Button size="lg" variant="outline" className="w-full" onClick={() => setIsReleaseOpen(true)} data-id="element-83" data-mp="3dgc5uvxkcg9j">
                  Release Loan
                </Button>
              </div>
            </motion.div> : <motion.div key="confirm" initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: -8
            }} transition={{
              duration: 0.2,
              ease: EASE
            }} className="flex flex-1 flex-col gap-4" data-id="element-84" data-mp="7qwjlky8s9b">
              <div className="flex items-center gap-3 rounded-2xl border border-brand-200 bg-white p-4 text-brand-500" data-id="element-85" data-mp="2defehw5cxinm">
                <AlertTriangleIcon className="h-5 w-5 shrink-0" data-id="element-86" data-mp="2r5txw436flv7" />
                <div data-id="element-87" data-mp="2n3yyj54t3ik2">
                  <p className="text-sm font-semibold" data-id="element-88" data-mp="2n3guvnfte224">
                    You are about to:{' '}
                    <span className="font-bold" data-id="element-89" data-mp="1zdwad040iwqq">{selectedOutcome}</span>
                  </p>
                  <p className="text-xs text-brand-400" data-id="element-90" data-mp="1ufl22ttd8uam">
                    This action will be recorded in the loan history.
                  </p>
                </div>
              </div>
              <div className="flex flex-1 flex-col" data-id="element-91" data-mp="23fzq40xpsnht">
                <label htmlFor="resolution-notes" className="mb-2 block text-sm font-semibold text-brand-500" data-id="element-92" data-mp="23eg742kobycc">
                  Resolution Notes{' '}
                  <span className="font-normal text-brand-400" data-id="element-93" data-mp="3euwpo4riyv3f">(Optional)</span>
                </label>
                <textarea id="resolution-notes" value={resolutionNotes} onChange={e => setResolutionNotes(e.target.value)} placeholder="Add any context about this decision..." className="min-h-[96px] w-full flex-1 resize-none rounded-xl border border-brand-200 bg-white p-3.5 text-sm text-brand-500 placeholder:text-brand-300 focus:border-accent-500 focus:outline-none focus:ring-2 focus:ring-accent-500" data-id="element-94" data-mp="2tpxft7sa2cr" />
              </div>
              <div className="flex items-center justify-between" data-id="element-95" data-mp="vgkeeg5e6jlh">
                <Button variant="ghost" onClick={handleCancel} disabled={isSubmitting} data-id="element-96" data-mp="vhbyoutjguwg">
                  Back
                </Button>
                <Button size="lg" onClick={handleConfirm} isLoading={isSubmitting} data-id="element-97" data-mp="2xa0a3rgc6onb">
                  Confirm &amp; Resolve
                </Button>
              </div>
            </motion.div>}
        </AnimatePresence>
        </div>
        </aside>
      </div>

      <ReleaseLoanModal isOpen={isReleaseOpen} hasNextLoan={hasNextLoan} onClose={() => setIsReleaseOpen(false)} onEndSession={details => {
      handleAddNote(`Released — ${details.disposition}. ${details.note}`);
      setIsReleaseOpen(false);
      onRelease(loan.id);
    }} onClaimNext={details => {
      handleAddNote(`Released — ${details.disposition}. ${details.note}`);
      setIsReleaseOpen(false);
      onReleaseAndClaimNext(loan.id);
    }} data-id="element-98" data-mp="2jzojwxoolgdy" />
    </motion.article>;
}